import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysInvoiceReprocessing } from '../queryKeys';

import { fetchInvoiceParsingInput } from './fetchInvoiceParsingInput';
import { type InvoiceParsingInputResponse } from './types';

/**
 * React Query based custom hook for getting the parsing input of an invoice process based on the process ID.
 * @param processId - The ID of the process to fetch the parsing input for.
 * @param options - Optional configuration for the query.
 * @returns The query result containing the invoice parsing input data.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/get_parsing_input_invoice_process__process_id__parsing_input_get
 */
export const useQueryInvoiceParsingInput = (
  processId: UUID,
  options?: Omit<
    UseQueryOptions<InvoiceParsingInputResponse | undefined>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<InvoiceParsingInputResponse | undefined>({
    enabled: Boolean(processId),
    queryFn: async () => fetchInvoiceParsingInput({ processId }),
    queryKey: queryKeysInvoiceReprocessing.getParsingInput(processId),
    staleTime: ms('30s'),
    ...options,
  });
};
